import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DefaultCardAction, Template } from './card/card.component';
import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { PaginatedSearcherService } from './paginated-searcher.service';
import { Scrollable } from '@vendasta/rx-utils';
import { FormControl } from '@angular/forms';
import { debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { PAGE_ROUTES } from '../shared';
import { SENDER_EMAIL_LIB_TOKEN } from '../../dependencies';
import { Sender, SenderType } from '@vendasta/email';

interface ScrollableSearch {
  ownerId: string;
  search: string;
}

const DEFAULT_TEMPLATE_OWNER_ID_PARTNER = 'SYSTEM';
const DEFAULT_TEMPLATE_OWNER_ID_SMB = 'SYSTEM-SMB';

@Component({
  selector: 'email-library-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: false,
})
export class EmailLibraryListComponent implements OnInit {
  @Input() openAsReadOnly = false;
  @Input() ownerType: OwnerType;
  @Input() ownerId: string;
  @Input() userId: string;
  @Output() readOnlyEmailClicked: EventEmitter<Template> = new EventEmitter<Template>();

  searchField: FormControl = new FormControl();
  public myTemplatesDefaultAction: DefaultCardAction;

  private readonly loadMoreTrigger$$ = new Subject<null>();

  readonly AppNamespace = AppNamespace.EMAIL_LIBRARY;

  readonly search$: Observable<string>;
  private readonly userAddedTemplate;
  readonly systemTemplates$: Observable<Template[]>;
  readonly templates: Scrollable<Template, ScrollableSearch>;
  readonly templates$: Observable<Template[]>;

  public showingAllTemplates$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private readonly searcher: PaginatedSearcherService,
    private readonly router: Router,
    @Inject(SENDER_EMAIL_LIB_TOKEN) private sender$: Observable<Sender>,
  ) {
    const defaultOwnerId$ = this.sender$.pipe(
      map((sender) => {
        if (sender.type === SenderType.SENDER_TYPE_BUSINESS) {
          return DEFAULT_TEMPLATE_OWNER_ID_SMB;
        } else {
          return DEFAULT_TEMPLATE_OWNER_ID_PARTNER;
        }
      }),
    );
    this.search$ = this.searchField.valueChanges.pipe(
      tap((search) => {
        if (search !== '') {
          this.showingAllTemplates$$.next(false);
        } else {
          this.showingAllTemplates$$.next(true);
        }
      }),
      startWith(''),
      debounceTime(500),
    );

    this.userAddedTemplate = router.getCurrentNavigation()?.extras?.state?.template;

    this.systemTemplates$ = defaultOwnerId$.pipe(
      switchMap((defaultOwnerId) =>
        this.searcher.loadPage(OwnerType.OWNER_TYPE_SYSTEM, defaultOwnerId, AppNamespace.EMAIL_LIBRARY, '', null),
      ),
      map((resp) => resp.items),
    );
    this.templates = new Scrollable<Template, ScrollableSearch>(
      this.search$.pipe(map((search) => ({ ownerId: this.ownerId, search }))),
      ({ ownerId, search }, cursor) => {
        return this.searcher.loadPage(this.ownerType, ownerId, this.AppNamespace, search, cursor);
      },
      this.loadMoreTrigger$$,
    );

    this.templates$ = this.templates.items$.pipe(
      map((items) => {
        if (this.userAddedTemplate && !items.some((item) => item.templateId === this.userAddedTemplate.templateId)) {
          return [this.userAddedTemplate, ...items];
        } else {
          return items;
        }
      }),
    );
  }

  ngOnInit(): void {
    this.myTemplatesDefaultAction = this.openAsReadOnly ? DefaultCardAction.USE : DefaultCardAction.EDIT;
  }

  loadMore(): void {
    this.loadMoreTrigger$$.next(null);
  }

  handleReadOnlyEmailClicked(template: Template): void {
    this.readOnlyEmailClicked.emit(template);
  }

  protected readonly DefaultCardAction = DefaultCardAction;
  protected readonly PAGE_ROUTES = PAGE_ROUTES;
  protected readonly length = length;
}
